// Centralized role + permission model for the frontend.
//
// Mirrors code/backend/core/roles.py — keep the two in sync. The backend
// is the authoritative enforcer; the frontend uses these values purely for
// UI gating (hide tabs, hide buttons, show Unauthorized page).
//
// A user signed in but in no recognized group is "unknown" — denied at
// both layers. Unauthenticated users never reach this code (SWA gates the
// entire app).

export const Role = Object.freeze({
  SYSTEM_ADMIN:    'system_admin',
  BILLING_ACCESS:  'billing_access',
  VIEW_ONLY:       'view_only',
  UNKNOWN:         'unknown',
  UNAUTHENTICATED: 'unauthenticated',
});

export const Permission = Object.freeze({
  VIEW:                'view',
  VIEW_AUDIT_LOG:      'view_audit_log',
  EDIT_RULES:          'edit_rules',
  EDIT_REFERENCE_DATA: 'edit_reference_data',
  APPROVE_RECORDS:     'approve_records',
  PUSH_TO_NETSUITE:    'push_to_netsuite',
  RUN_PIPELINE:        'run_pipeline',
  REPROCESS_BILLING:   'reprocess_billing',
  ADMIN_CONFIG:        'admin_config',
});

const ROLE_PERMISSIONS = {
  [Role.SYSTEM_ADMIN]: new Set([
    Permission.VIEW,
    Permission.VIEW_AUDIT_LOG,
    Permission.EDIT_RULES,
    Permission.EDIT_REFERENCE_DATA,
    Permission.APPROVE_RECORDS,
    Permission.PUSH_TO_NETSUITE,
    Permission.RUN_PIPELINE,
    Permission.REPROCESS_BILLING,
    Permission.ADMIN_CONFIG,
  ]),
  [Role.BILLING_ACCESS]: new Set([
    Permission.VIEW,
    Permission.EDIT_RULES,
    Permission.EDIT_REFERENCE_DATA,
    Permission.APPROVE_RECORDS,
    Permission.PUSH_TO_NETSUITE,
    Permission.RUN_PIPELINE,
    Permission.REPROCESS_BILLING,
  ]),
  [Role.VIEW_ONLY]: new Set([
    Permission.VIEW,
  ]),
  [Role.UNKNOWN]:         new Set(),
  [Role.UNAUTHENTICATED]: new Set(),
};

// Group object IDs — Entra security groups, single-tenant.
// Defaults match the production PSC GUIDs; can be overridden at build
// time via REACT_APP_ENTRA_GROUP_* env vars when needed.
const GROUP_IDS = {
  systemAdmin:   process.env.REACT_APP_ENTRA_GROUP_SYSTEM_ADMIN
                  || '191800bb-32f1-459b-8441-7bc06a080e4b',
  billingAccess: process.env.REACT_APP_ENTRA_GROUP_BILLING_ACCESS
                  || '2d684b35-8e4a-4541-b25d-0b2ce40bcc15',
  viewOnly:      process.env.REACT_APP_ENTRA_GROUP_VIEW_ONLY
                  || '34f13fd8-46bd-47bc-b72e-b4b45cb5d315',
};

// Resolve the strongest matching role.
// Precedence: system_admin > billing_access > view_only > unknown.
// Multi-group users always collapse to the strongest role — adding admin
// to someone's groups should never down-grade them.
export function resolveRole(groupIds) {
  if (!Array.isArray(groupIds) || groupIds.length === 0) return Role.UNKNOWN;
  const ids = new Set(groupIds.map((g) => String(g).toLowerCase()));
  if (ids.has(GROUP_IDS.systemAdmin.toLowerCase()))   return Role.SYSTEM_ADMIN;
  if (ids.has(GROUP_IDS.billingAccess.toLowerCase())) return Role.BILLING_ACCESS;
  if (ids.has(GROUP_IDS.viewOnly.toLowerCase()))      return Role.VIEW_ONLY;
  return Role.UNKNOWN;
}

export function rolePermissions(role) {
  return ROLE_PERMISSIONS[role] || new Set();
}

export function hasPermission(role, permission) {
  return (ROLE_PERMISSIONS[role] || new Set()).has(permission);
}
